import { useMemo } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { usePlanningWizard } from '../PlanningWizardContext';
import { displayDate } from '../dateUtils';
import { Option, STAFF_COLUMNS } from '../types';

/* eslint-disable @typescript-eslint/no-explicit-any */

const labelOf = (list: Option[], value: string) =>
  list.find((o) => o.value === value)?.label ?? '';

const MINORITY_TEXT: Record<string, string> = {
  '1': 'Minority',
  '2': 'Non-Minority',
  '3': 'Opened',
};

/**
 * Step 10 — Confirmation (Angular confirmationDetails display + confirmation
 * card). All display values are derived from context; Confirm runs finalSubmit.
 */
const StepConfirmation = () => {
  const {
    detailsForm,
    options,
    allDirectorate,
    isSCERT,
    schType,
    counsellingMinority,
    vacancyMinority,
    level1Approver,
    level2Approver,
    vacUpdatedBy,
    categoryList,
    managementList,
    panelList,
    staffList,
    addedPriorityList,
    addedSeniorityList,
    selectedSchCategory,
    selectedSchManage,
    selectedSchPanel,
    selectedVacCategory,
    selectedVacManage,
    selectedVacPanel,
    previousPage,
    finalSubmit,
  } = usePlanningWizard();

  const v = detailsForm.values;
  const counsellingType = String(v.CounsellingType);

  const byIds = (list: any[], ids: string[]) =>
    list.filter((row) => ids.includes(String(row.id)));

  const confirm = useMemo(
    () => ({
      directorate: labelOf(allDirectorate, String(v.Directorate)),
      location: labelOf(options.locations, String(v.Location)),
      counsellingType: labelOf(options.counsellingTypes, counsellingType),
      candidate: labelOf(options.vacancyLevels, String(v.CandidateUpdatedBy)),
      vacUpdatedBy: labelOf(options.vacancyLevels, vacUpdatedBy),
      level1: labelOf(options.level1, level1Approver),
      level2: labelOf(options.level2, level2Approver),
      schCategory: byIds(categoryList, selectedSchCategory),
      schManage: byIds(managementList, selectedSchManage),
      schPanel: byIds(panelList, selectedSchPanel),
      vacCategory: byIds(categoryList, selectedVacCategory),
      vacManage: byIds(managementList, selectedVacManage),
      vacPanel: byIds(panelList, selectedVacPanel),
    }),
    [
      v,
      counsellingType,
      allDirectorate,
      options,
      vacUpdatedBy,
      level1Approver,
      level2Approver,
      categoryList,
      managementList,
      panelList,
      selectedSchCategory,
      selectedSchManage,
      selectedSchPanel,
      selectedVacCategory,
      selectedVacManage,
      selectedVacPanel,
    ]
  );

  const labelTable = (title: string, rows: any[]) => (
    <div className="col">
      <DataTable value={rows} showGridlines responsiveLayout="scroll">
        <Column
          header="S.No"
          body={(_r, o) => o.rowIndex + 1}
          style={{ width: '4rem' }}
        />
        <Column field="label" header={title} />
      </DataTable>
    </div>
  );

  return (
    <div className="card p-4">
      <div className="row">
        <div className="col">
          <b className="fs-6">Are you sure you want to submit ?</b>
        </div>
      </div>

      <div className="row mt-2">
        <div className="col">
          <b>From</b>
        </div>
        <div className="col">{displayDate(v.FromDate)}</div>
        <div className="col">
          <b>To</b>
        </div>
        <div className="col">{displayDate(v.ToDate)}</div>
      </div>

      <div className="row mt-2">
        <div className="col">
          <b>Title</b>
        </div>
        <div className="col">{v.Title}</div>
        <div className="col">
          <b>Directorate</b>
        </div>
        <div className="col">{confirm.directorate}</div>
      </div>

      <div className="row mt-2">
        <div className="col">
          <b>Location</b>
        </div>
        <div className="col">{confirm.location}</div>
        <div className="col">
          <b>Counselling Type</b>
        </div>
        <div className="col">{confirm.counsellingType}</div>
      </div>

      <div className="row mt-2">
        <div className="col">
          <b>Approval Level 1</b>
        </div>
        <div className="col">{confirm.level1 || '-'}</div>
        <div className="col">
          <b>Approval Level 2</b>
        </div>
        <div className="col">{confirm.level2 || '-'}</div>
      </div>

      <div className="row mt-2">
        <div className="col-3">
          <b>Vacancy Updated By</b>
        </div>
        <div className="col-3">{confirm.vacUpdatedBy || '-'}</div>
        <div className="col-3">
          <b>Candidate Updated By</b>
        </div>
        <div className="col-3">{confirm.candidate || '-'}</div>
      </div>

      {v.Directorate !== '5' && v.Directorate !== '4' && (
        <div className="row mt-2">
          <div className="col-3">
            <b>School Type</b>
          </div>
          <div className="col-3">
            {schType === '1' ? 'Government' : schType === '2' ? 'Aided' : '-'}
          </div>
          {schType === '2' && (
            <>
              <div className="col-3">
                <b>School Minority Type</b>
              </div>
              <div className="col-3">
                {MINORITY_TEXT[counsellingMinority] ?? '-'}
              </div>
            </>
          )}
        </div>
      )}

      {schType === '2' && (
        <div className="row mt-2">
          <div className="col-3">
            <b>Vacancies Minority Type</b>
          </div>
          <div className="col-3">{MINORITY_TEXT[vacancyMinority] ?? '-'}</div>
        </div>
      )}

      <div className="row mt-2">
        <div className="col">
          <DataTable
            value={staffList}
            responsiveLayout="scroll"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 25]}
          >
            <Column
              header="S.No"
              body={(_r, o) => o.rowIndex + 1}
              style={{ width: '4rem' }}
            />
            {STAFF_COLUMNS.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                body={(row) => row[col.field] || '-'}
              />
            ))}
          </DataTable>
        </div>
        {labelTable(
          'Priority',
          addedPriorityList.filter((p) => p.label)
        )}
        {labelTable(
          'Seniority Order',
          addedSeniorityList.filter((s) => s.label)
        )}
      </div>

      {counsellingType !== '4' && (
        <>
          <div className="row mt-2">
            <div className="col">
              <b>{isSCERT ? 'Institution Category' : 'School Category'}</b>
            </div>
          </div>
          <div className="row mt-2">
            {labelTable('Category', confirm.schCategory)}
            {labelTable('Management', confirm.schManage)}
            {labelTable('Panel', confirm.schPanel)}
          </div>
        </>
      )}

      <div className="row mt-2">
        <div className="col">
          <b>Vacancy</b>
        </div>
      </div>
      <div className="row mt-2">
        {labelTable('Category', confirm.vacCategory)}
        {labelTable('Management', confirm.vacManage)}
        {labelTable('Panel', confirm.vacPanel)}
      </div>

      <div className="row mt-4">
        <div className="col-6">
          <Button
            type="button"
            icon="pi pi-angle-left"
            label="Previous"
            className="p-button-danger"
            onClick={previousPage}
          />
        </div>
        <div className="col-6 text-end">
          <Button
            type="button"
            icon="pi pi-angle-right"
            iconPos="right"
            label="Confirm"
            className="p-button-success"
            onClick={finalSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default StepConfirmation;
